import React, { memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Group, Text } from '@mantine/core';
import QuestSingleChoice from './QuestSingleChoice';
import QuestMultipleChoice from './QuestMultipleChoice';
import QuestConfirmation from './QuestConfirmation';
import QuestInput from './QuestInput';
import QuestStart from './QuestStart';
import InteractionHistory from './InteractionHistory';
import DotLoader from '../../DotLoader/DotLoader';
import PersistentTextArea from './PersistentTextArea';
import { InteractButton, InteractionContainer } from './InteractionHandlerCSS';
import { SubmitButton } from './PersistentTextAreaCSS';

// Composant pour l'interaction actuelle
const CurrentQuestion = memo(({ interaction }) => {
  if (!interaction) return null;

  switch (interaction.type) {
    case 'questSingleChoice':
      return <QuestSingleChoice />;
    case 'questMultChoice':
      return <QuestMultipleChoice />;
    case 'questConfirmation':
      return <QuestConfirmation />;
    case 'questInput':
      return <QuestInput />;
    case 'questStart':
      return <QuestStart />;
    default:
      return <div></div>;
  }
});

const InteractionHandler = () => {
  const currentInteraction = useSelector(
    (state) => state.interaction.currentInteraction
  );
  const interactionThread = useSelector(
    (state) => state.interaction.interactionThread
  );
  const isVisible = useSelector(
    (state) => state.interaction.interactionWindowVisible
  );

  const status = useSelector((state) => state.interaction.status);
  const server_action = useSelector(
    (state) => state.interaction.server_action || 'zzzzzz'
  );
  const questionId = currentInteraction?.data?.questionId || null;
  const dispatch = useDispatch();

  // Fonction pour démarrer une nouvelle conversation
  const startNewConversation = () => {
    // Réinitialise l'état de l'interaction et ouvre QuestStart
    if (questionId) {
      dispatch({
        type: 'INTERACTION_RESPONSE',
        payload: {
          questionId,
          response: JSON.stringify({
            source: 'newConversationButton',
            finish: true, // <— signal de fin côté outil
          }),
          isAlternative: true,
        },
      });
    }
    dispatch({
      type: 'INTERACTION_START_NEW',
      payload: {
        type: 'questStart',
        data: {
          question: 'Comment puis-je vous aider ?',
          questionId: `new-${Date.now()}`,
          description:
            'Posez votre question ou décrivez ce que vous souhaitez faire',
        },
      },
    });
  };

  const handleEndConversation = () => {
    if (!questionId) {
      // Variante minimaliste : on désactive si pas de question en cours
      // (sinon prévoir une action dédiée côté serveur p.ex. AGENT_SIGNAL_FINISH)
      alert('No active question to end from. Try when a question is open.');
      return;
    }

    dispatch({
      type: 'INTERACTION_RESPONSE',
      payload: {
        questionId,
        response: JSON.stringify({
          source: 'endButton',
          finish: true, // <— signal de fin côté outil
        }),
        isAlternative: true,
        finish: true, // for local
      },
    });
  };

  // Si rien n'est visible, ne rien rendre
  if (!isVisible && !interactionThread.length) return null;

  const shouldShowCurrentQuestion =
    isVisible && status === 'wait_user' && currentInteraction;
  const showRestartButton =
    interactionThread.length > 0 ||
    (status === 'wait_server' && !currentInteraction);

  return (
    <InteractionContainer>
      {/* Afficher l'historique des interactions */}
      {interactionThread.length > 0 && (
        <InteractionHistory thread={interactionThread} />
      )}

      {/* Afficher l'interaction courante */}
      {shouldShowCurrentQuestion && (
        <CurrentQuestion interaction={currentInteraction} />
      )}

      {status === 'wait_server' && (
        <div
          style={{
            padding: '10px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginTop: '10px',
          }}
        >
          <Group direction="row">
            <DotLoader />
            <Text color="black">Server Action: {server_action}</Text>
          </Group>
        </div>
      )}
      {shouldShowCurrentQuestion &&
        currentInteraction.type !== 'questStart' && <PersistentTextArea />}
      {showRestartButton && (
        <>
          <InteractButton onClick={startNewConversation}>
            New conversation
          </InteractButton>
          <InteractButton
            onClick={handleEndConversation}
            disabled={!questionId}
            title={questionId ? 'End this conversation' : 'No active question'}
            style={{ opacity: questionId ? 1 : 0.5 }}
          >
            End Conversation
          </InteractButton>
        </>
      )}
    </InteractionContainer>
  );
};

export default InteractionHandler;
